// ky uses Object.hasOwn
import "core-js/actual/object/has-own";

import ky, { HTTPError, Options } from "ky";

import Log from "@repo/utils/Log";
import UFunction from "@repo/utils/UFunction";
import UObject from "@repo/utils/UObject";
import UPromise from "@repo/utils/UPromise";
import UType from "@repo/utils/UType";

export type Get<Req, Res> = {
  GET: (req: Req, options?: Options) => Promise<Res>;
};
export type Delete<Req = {}, Res = {}> = {
  DELETE: (req: Req, options?: Options) => Promise<Res>;
};
export type Put<Req, Res = {}> = {
  PUT: (req: Req, options?: Options) => Promise<Res>;
};
export type Post<Req, Res = {}> = {
  POST: (req: Req, options?: Options) => Promise<Res>;
};
export type Patch<Req, Res = {}> = {
  PATCH: (req: Req, options?: Options) => Promise<Res>;
};

export type Exchange<Request, Response> = {
  request: Request;
  response: Response;
};

export { HTTPError };

const methods = ["GET", "POST", "PUT", "DELETE", "PATCH"] as const;

type AnyMethod = Partial<
  Get<any, any> &
    Delete<any, any> &
    Put<any, any> &
    Post<any, any> &
    Patch<any, any>
>;

export type ApiSchema =
  | AnyMethod
  | {
      [key: string]: ApiSchema;
    };

namespace RestApi {
  export function create<Api extends ApiSchema>(
    baseUrl: string,
    http: typeof ky = ky
  ): Api {
    return new Proxy(
      {},
      {
        get(_, property: any) {
          if (!methods.includes(property))
            return create(`${baseUrl}/${property}`, http);

          return (body: any, options: Options & { stub?: any } = {}) => {
            const resolvedBody = UObject.transform.deep(
              body,
              ([key, value], next) => [
                key.replace(/[A-Z]/g, (s) => `_${s.toLowerCase()}`),
                value instanceof Date ? value.toISOString() : next(),
              ]
            );
            const res = options.stub
              ? Promise.resolve(options.stub)
              : http[
                  property.toLowerCase() as "get" | "post" | "put" | "delete"
                ](
                  property !== "GET"
                    ? baseUrl
                    : `${baseUrl}?${new URLSearchParams(
                        resolvedBody || {}
                      ).toString()}`,
                  {
                    ...(property !== "GET" && {
                      json: resolvedBody,
                    }),
                    ...options,
                  }
                )
                  .json()
                  .then((data: any) =>
                    UObject.transform.deep(data, ([key, value], next) => [
                      key.replace(/_[a-z0-9]/g, (s) => s[1]!.toUpperCase()),
                      key.endsWith("_at") ? new Date(value) : next(),
                    ])
                  );

            Log.withTrace(
              `${options.stub ? "STUB " : ""}%c[Rest] %c${property} %c${baseUrl}`,
              "color: green",
              "color: #af0",
              "color: #0af",
              resolvedBody,
              " -> ",
              res
            );

            return res;
          };
        },
      }
    ) as Api;
  }

  export const attachStubs = <Api extends ApiSchema>(
    api: Api,
    stubs: Stubs<Api>
  ) =>
    new Proxy(
      {},
      {
        get(_, property: any): any {
          if (!methods.includes(property))
            return attachStubs(
              // @ts-expect-error
              api[property],
              // @ts-expect-error
              stubs?.[property] ||
                // @ts-expect-error
                (stubs?.[
                  Object.keys(stubs).find((key) => /^\w+_\[\w+\]$/.test(key))!
                ] as any)
            );

          // @ts-expect-error
          const stub = stubs?.[property];
          // @ts-expect-error
          if (!stub) return api[property];

          return (req: any, options: Options = {}) => {
            const res = stub({
              req,
              options,
              makeRequest: (_req = req, _options = options) =>
                // @ts-expect-error
                api[property](_req, _options),
            });

            return UPromise.delay(200).then(() => res);
          };
        },
      }
    ) as Api;

  export type Stubs<Api extends ApiSchema> = {
    [Key in keyof Api]?: Api[Key] extends (
      req: infer Req,
      options?: Options
    ) => Promise<infer Res>
      ? (context: {
          // params: Record<string, string>;
          req: Req;
          options?: Options;
          makeRequest: UFunction.OptionalArgs<Api[Key]>;
        }) => UPromise.Maybe<Res>
      : Stubs<UType.Narrow<Api[Key], ApiSchema>>;
  };

  export type ResponseOf<
    Method extends (req: any, options?: Options) => Promise<any>,
  > = Method extends (req: any, options?: Options) => Promise<infer Res>
    ? Res
    : never;
}
export default RestApi;
